// components/EquationCards.js

import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScaleBalanced, faScaleUnbalanced } from '@fortawesome/free-solid-svg-icons';
import TotalCard from './TotalCard';
import { calculatePeriodDates } from '../utils/dateUtils';

const EquationCards = ({ 
  trackingPeriod, 
  state, 
  setShowUpdateBalance 
}) => {
  const [showDifference, setShowDifference] = useState(false);
  // Calculate outgoing amounts based on selected period
  const calculateOutgoing = () => {
    let outgoingAmount = 0;
    
    if (trackingPeriod === 'current-week') {
      // Original current week logic: use state.totals.totalThisWeek (includes overdue)
      outgoingAmount = parseFloat(state.totals.totalThisWeek || 0);
    } else if (trackingPeriod === 'current-fortnight') {
      // Current Fortnight: Always includes BOTH weeks of the actual fortnight (week 1 + week 2)
      const firstWeekStart = new Date(state.fortnightDates.firstWeekStart);
      const firstWeekEnd = new Date(state.fortnightDates.firstWeekEnd);
      const secondWeekStart = new Date(state.fortnightDates.secondWeekStart);
      const secondWeekEnd = new Date(state.fortnightDates.secondWeekEnd);
      
      // Get first week unpaid bills (exclude pays)
      const firstWeekBills = (state.bills || []).filter(b => {
        const billDate = new Date(b.due_date);
        return billDate >= firstWeekStart && billDate <= firstWeekEnd && !b.paid && b.description !== 'incoming pay';
      });
      const firstWeekTotal = firstWeekBills.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
      
      // Get second week unpaid bills (exclude pays) 
      const secondWeekBills = (state.bills || []).filter(b => {
        const billDate = new Date(b.due_date);
        return billDate >= secondWeekStart && billDate <= secondWeekEnd && !b.paid && b.description !== 'incoming pay';
      });
      const secondWeekTotal = secondWeekBills.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
      
      // Prior overdue unpaid (already filtered to this bank account upstream)
      const overdueUnpaid = (state.overdueBills || []).filter(b => !b.paid);
      const overdueTotal = overdueUnpaid.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
      
      outgoingAmount = firstWeekTotal + secondWeekTotal + overdueTotal;
    } else if (trackingPeriod === 'two-weeks') {
      // Two weeks = always current week + next week bills (from the bill tables)
      const unpaidThisWeek = (state.currentWeekBills || []).filter(b => !b.paid && b.description !== 'incoming pay');
      const unpaidThisWeekTotal = unpaidThisWeek.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
      const unpaidNextWeek = (state.nextWeekBills || []).filter(b => !b.paid && b.description !== 'incoming pay');
      const unpaidNextWeekTotal = unpaidNextWeek.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
      // Include overdue bills as well
      const overdueUnpaid = (state.overdueBills || []).filter(b => !b.paid);
      const overdueTotal = overdueUnpaid.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
      outgoingAmount = unpaidThisWeekTotal + unpaidNextWeekTotal + overdueTotal;
    } else {
      // For multi-week periods (three weeks, four weeks)
      const periodInfo = calculatePeriodDates(trackingPeriod, state.fortnightDates);
      const periodStart = new Date(periodInfo.start);
      const periodEnd = new Date(periodInfo.end);
      
      // Filter bills within the period date range
      const periodBills = (state.bills || []).filter(b => {
        const billDate = new Date(b.due_date);
        return billDate >= periodStart && billDate <= periodEnd && !b.paid && b.description !== 'incoming pay';
      });
      const periodTotal = periodBills.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
      
      // Include overdue bills as well
      const overdueUnpaid = (state.overdueBills || []).filter(b => !b.paid);
      const overdueTotal = overdueUnpaid.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
      
      outgoingAmount = periodTotal + overdueTotal;
    }
    
    return outgoingAmount;
  };

  // Calculate incoming amounts based on selected period
  const calculateIncoming = () => {
    let incomingTotal = 0;
    
    if (trackingPeriod === 'current-week') {
      // Original current week logic: simple totalUnpaidPaysThisWeek
      incomingTotal = parseFloat(state.transferTotals.totalUnpaidPaysThisWeek || 0);
    } else if (trackingPeriod === 'current-fortnight') {
      // Original fortnight logic: include fortnight transfers + overdue transfers for actual fortnight
      const fortnightTransfers = parseFloat(state.transferTotals.totalUnpaidPaysThisWeek || 0) + parseFloat(state.transferTotals.totalUnpaidPaysNextWeek || 0);
      
      // Add previously unpaid transfers (overdue transfers)
      const overdueTransfers = (state.pays || []).filter(pay => {
        if (!pay || pay.transferMade || pay.paid) return false;
        const payDate = new Date(pay.due_date || pay.next_pay);
        const currentWeekStart = new Date(state.fortnightDates.firstWeekStart);
        return payDate <= currentWeekStart;
      });
      const overdueTransfersTotal = overdueTransfers.reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || t.transferAmount || 0) || 0), 0);
      
      incomingTotal = fortnightTransfers + overdueTransfersTotal;
    } else if (trackingPeriod === 'two-weeks') {
      // Two weeks = always current week + next week transfers (regardless of fortnight position)
      const twoWeeksTransfers = parseFloat(state.transferTotals.totalUnpaidPaysThisWeek || 0) + parseFloat(state.transferTotals.totalUnpaidPaysNextWeek || 0);
      
      // Add overdue transfers
      const overdueTransfers = (state.pays || []).filter(pay => {
        if (!pay || pay.transferMade || pay.paid) return false;
        const payDate = new Date(pay.due_date || pay.next_pay);
        const currentWeekStart = new Date(state.fortnightDates.firstWeekStart);
        return payDate <= currentWeekStart;
      });
      const overdueTransfersTotal = overdueTransfers.reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || t.transferAmount || 0) || 0), 0);
      
      incomingTotal = twoWeeksTransfers + overdueTransfersTotal;
    } else {
      // For multi-week periods (three weeks, four weeks)
      const fortnightTransfers = parseFloat(state.transferTotals.totalUnpaidPaysThisWeek || 0) + parseFloat(state.transferTotals.totalUnpaidPaysNextWeek || 0);
      
      const overdueTransfers = (state.pays || []).filter(pay => {
        if (!pay || pay.transferMade || pay.paid) return false;
        const payDate = new Date(pay.due_date || pay.next_pay);
        const currentWeekStart = new Date(state.fortnightDates.firstWeekStart);
        return payDate <= currentWeekStart;
      });
      const overdueTransfersTotal = overdueTransfers.reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || t.transferAmount || 0) || 0), 0);
      
      incomingTotal = fortnightTransfers + overdueTransfersTotal;
    }
    
    return incomingTotal;
  };

  const outgoingAmount = calculateOutgoing();
  const incomingAmount = calculateIncoming();
  const currentBalance = parseFloat(state.currentBalance || 0);
  const closingBalance = currentBalance - outgoingAmount + incomingAmount;
  const isNegative = closingBalance < 0;

  // Calculate the difference (net change)
  const difference = incomingAmount - outgoingAmount;

  return (
    <>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '15px',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        {/* Current Balance Card */}
        <div style={{ flex: '0 0 auto' }}>
          <Button
            variant="link"
            onClick={() => setShowUpdateBalance(true)}
            style={{
              padding: 0,
              border: 'none',
              backgroundColor: 'transparent',
              color: '#333',
              cursor: 'pointer',
              textAlign: 'left',
              fontWeight: 'bold',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.transform = 'scale(1.04)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <TotalCard
              title="Current Balance"
              amount={state.currentBalance}
            />
          </Button>
        </div>

        {/* Minus + Outgoing Card Group */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '15px',
          flex: '0 0 auto'
        }}>
          <div style={{ 
            fontSize: '2rem', 
            fontWeight: 'bold', 
            color: '#666'
          }}>
            −
          </div>
          <TotalCard
            title="Outgoing"
            amount={outgoingAmount.toFixed(2)}
            highlight={'highlight'}
          />
        </div>

        {/* Plus + Incoming Card Group */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '15px',
          flex: '0 0 auto'
        }}>
          <div style={{ 
            fontSize: '2rem', 
            fontWeight: 'bold', 
            color: '#666'
          }}>
            +
          </div>
          <TotalCard
            title="Incoming"
            amount={incomingAmount.toFixed(2)}
          />
        </div>

        {/* Equals + Closing Balance Card Group */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '15px',
          flex: '0 0 auto'
        }}>
          <div style={{ 
            fontSize: '2rem', 
            fontWeight: 'bold', 
            color: '#666'
          }}>
            =
          </div>
          <TotalCard
            title="Closing Balance"
            amount={closingBalance.toFixed(2)}
            highlight={isNegative ? '' : 'highlight'}
            isNegative={isNegative}
          />
        </div>

        {/* Balance/Scale Icon */}
        <div style={{ flex: '0 0 auto', marginLeft: '15px' }}>
          <Button 
            onClick={() => setShowDifference(!showDifference)} 
            style={{ 
              backgroundColor: '#A0C2BB',
              borderColor: '#A0C2BB',
              color: 'white',
              borderRadius: '50%',
              width: '45px',
              height: '45px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#8FB3AC';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#A0C2BB';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <FontAwesomeIcon 
              icon={showDifference ? faScaleBalanced : faScaleUnbalanced} 
              size="lg"
            />
          </Button>
        </div>
      </div>

      {/* Collapsible Difference Card */}
      {showDifference && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '20px', 
          transition: 'all 0.3s ease' 
        }}>
          <div style={{ flex: '0 0 auto' }}>
            <TotalCard
              title={trackingPeriod === 'current-week' ? 'Difference' : 'Net Change'}
              amount={difference.toFixed(2)}
              highlight={difference >= 0 ? 'secondary' : ''}
              isNegative={difference < 0}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default EquationCards;
